import { useEffect, useMemo, useRef, useState, type ElementType } from "react";
import { Link } from "react-router-dom";
import { listJobs } from "../api";
import { PageHelpHandle } from "../components/PageHelpHandle";
import { havocTier, HAVOC_SCORE_EXPLAINER } from "../lib/commentary";
import type { JobRow } from "../types";
// Crime-type icons for the per-card watermark scatter. Vite imports
// resolve at build time, so the bundle gets the hashed URLs.
import iconDrug from "../assets/offense-icons/drug.png";
import iconOther from "../assets/offense-icons/other.png";
import iconProperty from "../assets/offense-icons/property.png";
import iconPublicOrder from "../assets/offense-icons/public-order.png";
import iconVehicle from "../assets/offense-icons/vehicle.png";
import iconWeapon from "../assets/offense-icons/weapon.png";

// Server's `categorizeOffense` returns display-string buckets (see
// src/server/lib/severity.ts). Map each onto the closest PNG glyph
// in src/client/assets/offense-icons. Buckets without a perfect
// glyph reuse the most-aligned available one.
const CATEGORY_ICONS: Record<string, string> = {
  "Driving / License":   iconVehicle,
  "Drugs":               iconDrug,
  "Violence / Weapons":  iconWeapon,
  "Property / Theft":    iconProperty,
  "Public Order":        iconPublicOrder,
  "Parking / Paperwork": iconOther,
  "Other":               iconOther,
};

// Fixed scatter positions (top%, left%, rotate°, size px) — up to 8
// slots, one per possible category. Deterministic so the watermark
// pattern doesn't reflow on every render. Categories that aren't
// present on a person just don't render their icon.
const ICON_SLOTS: Array<{ top: string; left: string; rot: number; size: number }> = [
  { top: "10%",  left: "78%", rot: -8,  size: 88 },
  { top: "62%",  left: "12%", rot: 6,   size: 96 },
  { top: "30%",  left: "44%", rot: -14, size: 76 },
  { top: "78%",  left: "62%", rot: 10,  size: 84 },
  { top: "8%",   left: "20%", rot: 14,  size: 70 },
  { top: "48%",  left: "82%", rot: -6,  size: 78 },
  { top: "85%",  left: "30%", rot: -10, size: 72 },
  { top: "22%",  left: "8%",  rot: 8,   size: 80 },
];

function CardCrimeWatermarks({ categories }: { categories: string[] | undefined }) {
  if (!categories || categories.length === 0) return null;
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden"
      style={{ zIndex: 0 }}
    >
      {categories.slice(0, ICON_SLOTS.length).map((cat, i) => {
        const src = CATEGORY_ICONS[cat];
        if (!src) return null;
        const slot = ICON_SLOTS[i];
        return (
          <img
            key={cat}
            src={src}
            alt=""
            style={{
              position: "absolute",
              top: slot.top,
              left: slot.left,
              width: slot.size,
              height: slot.size,
              transform: `translate(-50%, -50%) rotate(${slot.rot}deg)`,
              opacity: 0.09,
              // Force the icon to render in white so it reads as a
              // wallpaper detail against the colored card bg, not a
              // tiny photograph.
              filter: "brightness(0) invert(1)",
            }}
          />
        );
      })}
    </div>
  );
}

// Continuous score-based gradient: forest green at 0 → amber-gold at
// mid → full bordeaux red at 100. HSL-interpolated through yellow
// (going BACKWARD around the wheel: 140° → 50° → 355°) so the
// transition reads as a natural traffic-light spectrum without ever
// passing through the "neon green" or "carnival yellow" zones. Each
// person's tile is uniquely colored to its severity.
function tierBgGradient(score: number | null | undefined): { bg: string; sub: string } {
  if (score === null || score === undefined) {
    // No score yet — neutral slate so the unscored tile reads as
    // "data still gathering" rather than "calm green."
    return {
      bg: "linear-gradient(155deg, #2A2C33 0%, #1A1C22 100%)",
      sub: "rgba(255,255,255,0.6)",
    };
  }
  const t = Math.max(0, Math.min(100, score)) / 100;
  // Two-stage hue interpolation:
  //   stage A (t 0→0.5): 140° green → 50° gold
  //   stage B (t 0.5→1): 50° gold → 355° bordeaux (going via 0°)
  let h: number;
  if (t < 0.5) {
    h = 140 - (t / 0.5) * 90; // 140 → 50
  } else {
    h = 50 - ((t - 0.5) / 0.5) * 55; // 50 → -5
    if (h < 0) h += 360; // wrap to 355
  }
  // Same drenched HSL in both themes — the saturated tier color IS
  // the tile's identity, and the pastel light-mode variant washed
  // out so badly the people-page lost its punch. Keep it loud.
  const s = 52;
  const l = 32;
  return {
    bg: `linear-gradient(155deg, hsl(${h}, ${s}%, ${l}%) 0%, hsl(${h}, ${s + 4}%, ${l - 14}%) 100%)`,
    sub: "rgba(255,255,255,0.82)",
  };
}

function formatDate(s: string | null): string {
  if (!s) return "—";
  return new Date(s).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatTime(s: string | null): string {
  if (!s) return "";
  return new Date(s).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

type LedgerView = "cards" | "table";

const LEDGER_VIEW_KEY = "ledgerView";

export function RecordsPage() {
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [view, setView] = useState<LedgerView>(() => {
    try {
      const v = localStorage.getItem(LEDGER_VIEW_KEY);
      return v === "table" ? "table" : "cards";
    } catch {
      return "cards";
    }
  });
  const setViewPersisted = (v: LedgerView) => {
    setView(v);
    try { localStorage.setItem(LEDGER_VIEW_KEY, v); } catch {}
  };

  useEffect(() => {
    listJobs()
      .then(setJobs)
      .finally(() => setLoading(false));
  }, []);

  // Pre-filter list (no search query). We split this from the
  // post-filter list so we can show a "no results" empty state when
  // the query filtered everything out, distinct from the "no records
  // at all" state.
  const allCaptured = useMemo(
    () =>
      jobs.filter(
        (j) =>
          j.status === "completed" ||
          (j.record_count_captured !== null && (j.record_count_captured ?? 0) > 0),
      ),
    [jobs],
  );
  const captured = useMemo(() => {
    let xs = allCaptured;
    if (query.trim()) {
      const q = query.toLowerCase();
      xs = xs.filter(
        (j) =>
          `${j.first_name} ${j.last_name}`.toLowerCase().includes(q) ||
          j.date_of_birth.includes(q),
      );
    }
    // Sort: completed people first (newest completion at top), then
    // still-processing people at the bottom. Within each group, sort by
    // most-recent activity date desc. Reasoning: in-progress entries
    // shouldn't be interspersed with finished ones because their data
    // isn't final yet — Damon shouldn't have to scan past placeholders
    // to see whose record is ready to read.
    const isInProgress = (j: JobRow): boolean =>
      j.status === "queued" || j.status === "running" || j.status === "waiting_captcha";
    return [...xs].sort((a, b) => {
      const ap = isInProgress(a) ? 1 : 0;
      const bp = isInProgress(b) ? 1 : 0;
      if (ap !== bp) return ap - bp; // completed (0) before processing (1)
      // Sort by INTAKE date (created_at), not completion date. Most-
      // recently-added participants sit at the top — the question
      // operators care about is "who did we add lately?" not "whose
      // scrape finished most recently" (which can be noisy: re-scrapes
      // refresh completed_at).
      return b.created_at.localeCompare(a.created_at);
    });
  }, [allCaptured, query]);

  // Sticky-state detection for the controls bar. When the bar scrolls
  // up past the viewport top, an IntersectionObserver fires on the
  // sentinel just above it; we toggle a `data-stuck` attribute so the
  // CSS can animate into a compact pinned bar (backdrop, shadow,
  // slightly smaller paddings). Sentinel pattern is the standard way
  // to detect position:sticky engagement.
  const stickySentinelRef = useRef<HTMLDivElement | null>(null);
  const stickyBarRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const sentinel = stickySentinelRef.current;
    const bar = stickyBarRef.current;
    if (!sentinel || !bar) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        bar.dataset.stuck = entry.isIntersecting ? "false" : "true";
      },
      { rootMargin: "-1px 0px 0px 0px", threshold: [0, 1] },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="page-shell">
      {/* Single continuous card — bordeaux hero at top with the wave
          bottom, then the "All people" controls + list right below,
          all inside ONE page-card so the wave reads as the transition
          from hero to body (not a card with no bottom). The BTN grid
          that used to sit between them has been removed at user
          request: a case manager needs the search input and the list,
          not a hero-metric grid. */}
      <div className="page-card page-card-flush" data-tour="people-list">
        <div className="page-hero page-hero-wave" style={{ position: "relative" }}>
          <div style={{ position: "absolute", top: 16, right: 18 }}>
            <PageHelpHandle
              query="What is the People page for, and how should I use it to find someone?"
              topicId="the-njrc-program-flow"
            />
          </div>
          <div className="page-hero-eyebrow">People</div>
          <h1 className="page-hero-title">Everyone we've pulled records on.</h1>
          <p className="page-hero-caption">
            Every person we've captured, newest first.{" "}
            <span
              title={HAVOC_SCORE_EXPLAINER}
              style={{ borderBottom: "1px dotted rgba(255,255,255,0.55)", cursor: "help" }}
            >
              Havoc Score
            </span>{" "}
            is an internal triage signal: 0 clean, 100 cataclysm; tiers explained on hover.
            Click a name to read the full file.
          </p>
        </div>

        {/* Body — section title + sticky controls + list. All inside
            the same page-card as the hero so the wave at the bottom of
            the hero leads visually into the content below. */}
        <div style={{ padding: "32px clamp(16px, 3vw, 32px) 24px" }}>
          <h2 className="h-section" style={{ margin: "0 0 16px" }}>All people</h2>

          {/* Sentinel — invisible 1px element whose visibility we
              observe to detect when the sticky bar engages. Sits
              immediately above the bar so it leaves the viewport at
              the exact moment the bar would otherwise scroll off. */}
          <div ref={stickySentinelRef} style={{ height: 1, marginBottom: -1 }} aria-hidden />

          {/* Sticky controls bar — search + view toggle. position:sticky
              with top:0 pins to the viewport top once the user scrolls
              past. The data-stuck attribute (driven by the
              IntersectionObserver above) flips on/off CSS that adds a
              backdrop, shadow, and slightly tighter padding for the
              pinned state — so the transition reads as "settling into
              a toolbar" rather than "abruptly stuck". */}
          <div
            ref={stickyBarRef}
            data-stuck="false"
            className="records-sticky-controls"
            style={{
              position: "sticky",
              top: 0,
              zIndex: 5,
              padding: "10px 0",
              marginInline: "calc(-1 * clamp(16px, 3vw, 32px))",
              paddingInline: "clamp(16px, 3vw, 32px)",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 14,
              marginBottom: 20,
              transition:
                "background 220ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 220ms cubic-bezier(0.16, 1, 0.3, 1), padding 220ms cubic-bezier(0.16, 1, 0.3, 1), backdrop-filter 220ms cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          >
            {/* Search input — flexes to fill, anchors to left */}
            <div className="relative" style={{ flex: "1 1 280px", maxWidth: 480 }}>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name or DOB"
                className="input"
                style={{ paddingLeft: 40, width: "100%" }}
              />
              <span
                className="pointer-events-none absolute top-1/2 -translate-y-1/2 text-[color:var(--color-text-dim)]"
                style={{ left: 14, fontSize: 16 }}
              >
                ⌕
              </span>
            </div>

            {/* View-mode toggle — anchors to right of the row */}
            <div className="inline-flex rounded-lg border border-[color:var(--color-line)] p-1 text-sm" style={{ marginLeft: "auto" }}>
              {(
                [
                  ["cards", "Cards"],
                  ["table", "Table"],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setViewPersisted(key as LedgerView)}
                  className={`tap-bump rounded-md px-3 py-1.5 font-medium transition ${
                    view === key
                      ? "bg-[color:var(--color-bg-card-hi)] text-[color:var(--color-text)]"
                      : "text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

        {loading ? (
          <div className="space-y-2">
            <div className="skeleton h-12 w-full" />
            <div className="skeleton h-12 w-full" />
            <div className="skeleton h-12 w-full" />
          </div>
        ) : captured.length === 0 && allCaptured.length === 0 ? (
          <div
            className="card text-center"
            style={{
              padding: "clamp(48px, 6vw, 96px) clamp(20px, 4vw, 48px)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 18,
            }}
          >
            <div
              aria-hidden
              style={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                background: "color-mix(in oklab, var(--color-bordeaux-hi) 14%, transparent)",
                color: "var(--color-bordeaux-hi)",
                fontFamily: "var(--font-serif)",
                fontSize: 36,
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              +
            </div>
            <div className="display" style={{ fontSize: 28, lineHeight: 1.15, letterSpacing: "-0.01em" }}>
              No one's on the docket yet.
            </div>
            <p
              className="subline mx-auto"
              style={{ maxWidth: 480, color: "var(--color-text-muted)", fontSize: 14, lineHeight: 1.55 }}
            >
              Once you add someone via the intake form, the worker pulls their court, MVC, and corrections
              records and they'll show up here ranked by Havoc Score.
            </p>
            <Link
              to="/lookup"
              className="btn btn-primary"
              style={{ marginTop: 4, paddingInline: 22, paddingBlock: 12, fontSize: 13 }}
            >
              Add the first person →
            </Link>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("njrc:open-assistant", {
                detail: { query: "How do I add my first person to the docket?" },
              }))}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--color-text-dim)",
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                cursor: "pointer",
                marginTop: 4,
              }}
            >
              Or ask Jack how this works ›
            </button>
          </div>
        ) : captured.length === 0 ? (
          /* Records exist but the search filtered them all out. Show a
             distinct empty state so the user doesn't think their data
             vanished. */
          <div className="card text-center py-12">
            <div className="h2-tight text-[color:var(--color-text-dim)]">
              No matches for &ldquo;{query.trim()}&rdquo;.
            </div>
            <p className="subline mx-auto mt-2">
              Try a different name or DOB, {" "}
              <button
                type="button"
                onClick={() => setQuery("")}
                className="underline decoration-dotted underline-offset-4"
                style={{ background: "transparent", border: "none", color: "inherit", padding: 0, cursor: "pointer" }}
              >
                clear search
              </button>{" "}
              to see all {allCaptured.length} captured records.
            </p>
          </div>
        ) : view === "table" ? (
          /* Compact tabular view — five columns sized intentionally so name
             gets the room it needs and scores get a tight tabular column. */
          <div className="card overflow-x-auto p-0">
            <table className="w-full min-w-[680px] border-collapse">
              <thead>
                <tr className="border-b border-[color:var(--color-line)] text-left">
                  {[
                    { label: "Name", align: "left" as const },
                    { label: "Tier", align: "left" as const },
                    { label: "DOB", align: "left" as const },
                    { label: "Records", align: "right" as const },
                    { label: "Havoc", align: "right" as const },
                    { label: "Added", align: "left" as const },
                  ].map((h) => (
                    <th
                      key={h.label}
                      className={`px-4 py-4 font-semibold uppercase tracking-wider text-[color:var(--color-text-dim)] whitespace-nowrap ${h.align === "right" ? "text-right" : "text-left"}`}
                      style={{ fontSize: "clamp(11px, 0.9vw, 13px)", letterSpacing: "0.12em" }}
                    >
                      {h.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {captured.map((j) => {
                  const tier = havocTier(j.havoc_score ?? null);
                  const c = tierBgGradient(j.havoc_score);
                  const inProgress = j.status === "queued" || j.status === "running" || j.status === "waiting_captcha";
                  return (
                    <tr
                      key={j.id}
                      className={
                        inProgress
                          ? "group border-b border-[color:var(--color-line)] last:border-b-0"
                          : "group cursor-pointer border-b border-[color:var(--color-line)] last:border-b-0 hover:bg-[color:var(--color-bg-card-hi)] transition-colors"
                      }
                    >
                      <td className="px-4 py-5">
                        {inProgress ? (
                          <div
                            className="block"
                            title="Still capturing: page will be available once finished."
                            style={{ cursor: "wait", opacity: 0.78 }}
                          >
                            <div className="flex items-baseline gap-2.5">
                              <div
                                className="display text-[color:var(--color-text)]"
                                style={{
                                  fontSize: "clamp(18px, 1.5vw, 22px)",
                                  lineHeight: 1.1,
                                  fontVariationSettings: '"opsz" 36, "SOFT" 50',
                                }}
                              >
                                {j.last_name}, {j.first_name}
                              </div>
                              <ProcessingBadge />
                            </div>
                          </div>
                        ) : (
                          <Link to={`/records/${j.id}`} className="block">
                            <div className="flex items-baseline gap-2.5">
                              <div
                                className="display group-hover:underline text-[color:var(--color-text)]"
                                style={{
                                  fontSize: "clamp(18px, 1.5vw, 22px)",
                                  lineHeight: 1.1,
                                  fontVariationSettings: '"opsz" 36, "SOFT" 50',
                                }}
                              >
                                {j.last_name}, {j.first_name}
                              </div>
                            </div>
                          </Link>
                        )}
                      </td>
                      <td className="px-4 py-5">
                        {tier ? (
                          <span
                            className="havoc-tile rounded-full px-3 py-1 font-semibold uppercase tracking-[0.16em] whitespace-nowrap"
                            style={{ background: c.bg, color: "white", fontSize: "clamp(11px, 0.85vw, 13px)" }}
                          >
                            {tier.label}
                          </span>
                        ) : (
                          <span className="meta text-[color:var(--color-text-dim)]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-5 mono whitespace-nowrap" style={{ color: "var(--color-text)", fontSize: "clamp(13px, 1vw, 16px)" }}>
                        {j.date_of_birth}
                      </td>
                      <td className="px-4 py-5 text-right">
                        <span
                          className="display tabular text-[color:var(--color-text)]"
                          style={{ fontSize: "clamp(22px, 2vw, 28px)", lineHeight: 1, fontWeight: 700 }}
                        >
                          {j.record_count_captured ?? 0}
                        </span>
                      </td>
                      <td className="px-4 py-5 text-right">
                        <span
                          className="display tabular text-[color:var(--color-text)]"
                          style={{ fontSize: "clamp(22px, 2vw, 28px)", lineHeight: 1, fontWeight: 700 }}
                        >
                          {j.havoc_score ?? "—"}
                        </span>
                      </td>
                      <td className="px-4 py-5 whitespace-nowrap" style={{ color: "var(--color-text-muted)", fontSize: "clamp(13px, 1vw, 15px)" }}>
                        {formatDate(j.created_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <>
            {/* Card grid — solid colored tiles keyed to havoc tier, instantly
                readable at a glance on any device. */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {captured.map((j) => {
                const c = tierBgGradient(j.havoc_score);
                const tier = havocTier(j.havoc_score ?? null);
                const inProgress = j.status === "queued" || j.status === "running" || j.status === "waiting_captcha";
                // In-progress people are NOW clickable too — the person
                // page renders a Scrape Status panel up top showing per-
                // source progress, errors, and next-retry timing. The
                // ProcessingBadge + dimmed visual is enough warning that
                // the data isn't final, and the ability to drill in to
                // see WHY a scrape is still pending is more useful than
                // the lockout.
                const Wrapper: ElementType = Link;
                const wrapperProps = {
                  to: `/records/${j.id}`,
                  title: inProgress
                    ? "Still capturing: click to see per-source scrape status."
                    : undefined,
                  style: inProgress
                    ? { background: c.bg, color: "white", opacity: 0.78 }
                    : { background: c.bg, color: "white" },
                };
                return (
                  <Wrapper
                    key={j.id}
                    className={
                      inProgress
                        ? "havoc-tile group relative block overflow-hidden rounded-2xl border border-transparent"
                        : "havoc-tile group relative block overflow-hidden rounded-2xl border border-transparent transition-all duration-300 hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-black/40"
                    }
                    {...(wrapperProps as any)}
                  >
                    <CardCrimeWatermarks categories={j.categories} />
                    <div className="relative z-10 flex h-full flex-col p-5 sm:p-6">
                      <div className="flex items-center justify-between gap-2">
                        <div
                          className="text-[10px] font-semibold uppercase tracking-[0.22em]"
                          style={{ color: c.sub }}
                        >
                          {tier ? tier.label : "Unscored"}
                        </div>
                        {inProgress && <ProcessingBadge onSolid />}
                      </div>

                      {/* Big serif name */}
                      <h3
                        className="display mt-3 text-white"
                        style={{
                          fontSize: "clamp(26px, 2.6vw, 34px)",
                          lineHeight: 1.05,
                          letterSpacing: "-0.02em",
                          fontWeight: 700,
                          fontVariationSettings: '"opsz" 60, "SOFT" 50',
                          wordBreak: "break-word",
                        }}
                      >
                        {j.last_name}, {j.first_name}
                      </h3>

                      <div
                        className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1"
                        style={{ color: c.sub, fontSize: "clamp(12px, 0.95vw, 14px)" }}
                      >
                        <span className="mono">{j.date_of_birth}</span>
                        <span style={{ opacity: 0.6 }}>·</span>
                        <span>added {formatDate(j.created_at)}</span>
                      </div>

                      {/* Bottom strip: havoc score + record count */}
                      <div
                        className="mt-auto flex items-end justify-between gap-3 pt-6 border-t"
                        style={{ borderColor: "rgba(255,255,255,0.18)" }}
                      >
                        <div>
                          <div
                            className="font-semibold uppercase tracking-[0.24em]"
                            style={{ color: c.sub, fontSize: "clamp(10px, 0.8vw, 12px)" }}
                          >
                            Havoc
                          </div>
                          <div
                            className="display tabular text-white"
                            style={{
                              fontSize: "clamp(40px, 3.5vw, 52px)",
                              lineHeight: 1,
                              fontVariationSettings: '"opsz" 144, "SOFT" 30',
                              fontWeight: 700,
                            }}
                          >
                            {j.havoc_score ?? "—"}
                          </div>
                        </div>
                        <div className="text-right">
                          <div
                            className="font-semibold uppercase tracking-[0.24em]"
                            style={{ color: c.sub, fontSize: "clamp(10px, 0.8vw, 12px)" }}
                          >
                            Records
                          </div>
                          <div
                            className="display tabular text-white"
                            style={{
                              fontSize: "clamp(34px, 3vw, 44px)",
                              lineHeight: 1,
                              fontVariationSettings: '"opsz" 144, "SOFT" 30',
                              fontWeight: 700,
                            }}
                          >
                            {j.record_count_captured ?? 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Wrapper>
                );
              })}
            </div>
          </>
        )}
        </div>
      </div>
    </div>
  );
}

/**
 * Small pill that flags a person as "still being scraped". The whole pill
 * breathes (slow opacity blink, ~2s per cycle) to make it unmistakably
 * a "wait for it" signal — distinct from any static label. Two variants:
 * default (dark) for the table view, and `onSolid` (translucent over a
 * colored card background) for the cards.
 */
function ProcessingBadge({ onSolid = false }: { onSolid?: boolean }) {
  const baseStyle = onSolid
    ? {
        background: "rgba(255,255,255,0.18)",
        color: "rgba(255,255,255,0.95)",
        border: "1px solid rgba(255,255,255,0.25)",
      }
    : {
        color: "var(--color-orange-hi)",
        background: "var(--color-orange-soft)",
        border: "1px solid color-mix(in oklab, var(--color-orange-hi) 35%, transparent)",
      };
  return (
    <>
      <span
        className="processing-blink inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em]"
        style={baseStyle}
      >
        <span
          className="dot dot-pulse"
          style={{ background: onSolid ? "white" : "var(--color-orange-hi)" }}
        />
        Processing
      </span>
      <style>{`
        @keyframes processing-breathe {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.45; }
        }
        .processing-blink {
          animation: processing-breathe 2200ms ease-in-out infinite;
        }
      `}</style>
    </>
  );
}

